import { computed, inject, Injectable } from '@angular/core';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { DMS_FILE_SEGMENT_PATH, DMS_FILES_PATH, DMS_RECENT_FILES_PATH, DMS_ROOT_PATH, DMS_SEARCH_PATH, TABS_ROUTES } from '../utils/dms-routing.utils';
import { NavigationExtras, Router, UrlSegment } from '@angular/router';
import { IrisDmsGlobalStore } from '../store/dms-global.signal-store';
import { IrisDMSFileQueryParam, IrisDMSFileQueryParamAction } from '../models/IrisDMSFileQueryParam';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { firstValueFrom, map, switchMap, take } from 'rxjs';
import { IrisDMSFileI } from '../models/IrisDMSFile';

@Injectable({ providedIn: 'root' })
export class IrisDmsRoutingService {
  readonly globalSandbox = inject(IrisGlobalSandbox);
  readonly router = inject(Router);
  readonly dmsGlobalStore = inject(IrisDmsGlobalStore);
  readonly foldersService = inject(IrisFoldersService);

  readonly baseDmsPath = computed(() => {
    const appAlias = this.globalSandbox.appAlias();
    const projectId = this.globalSandbox.currentProjectId();
    const basePath = [appAlias, DMS_ROOT_PATH];

    if (!projectId) { return basePath; }
    return [...basePath, projectId];
  });

  readonly dmsFoldersPath = computed(() => [...this.baseDmsPath(), TABS_ROUTES.folders.path].join('/'));
  readonly dmsSystemFoldersPath = computed(() => [...this.baseDmsPath(), TABS_ROUTES.systemFolders.path].join('/'));
  readonly dmsRecentFilesPath = computed(() => [...this.baseDmsPath(), TABS_ROUTES.folders.path, DMS_RECENT_FILES_PATH].join('/'));

  private readonly projectSegmentIndex = 1;

  openMainPage(extras?: NavigationExtras): Promise<boolean> {
    this.dmsGlobalStore.resetSelectedFolderId();
    return this.router.navigate(this.baseDmsPath(), extras);
  }

  openFoldersViewPage(extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigate([...this.baseDmsPath(), TABS_ROUTES.folders.path], extras);
  }

  openFolderViewPage(folderId: string, extras?: NavigationExtras, keepFolderProject = false): Promise<boolean> {
    const requestFolderProjectId$ = this.foldersService.getFolderById(folderId).pipe(
      take(1),
      map(folder => folder.projectId),
    );
    const navigateWithRequest$ = (keepFolderProject ? requestFolderProjectId$ : this.globalSandbox.currentProjectId$).pipe(
      take(1),
      switchMap(projectId => {
        const segments = [this.globalSandbox.appAlias(), DMS_ROOT_PATH, projectId, TABS_ROUTES.folders.path, folderId, DMS_FILES_PATH];
        return this.router.navigate(segments, extras);
      }),
    );
    return firstValueFrom(navigateWithRequest$);
  }

  openSystemFoldersViewPage(extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigate([...this.baseDmsPath(), TABS_ROUTES.systemFolders.path], extras);
  }

  openSystemFolderViewPage(folderId: string, extras?: NavigationExtras): Promise<boolean> {
    const segments = [...this.baseDmsPath(), TABS_ROUTES.systemFolders.path, folderId, DMS_FILES_PATH];
    return this.router.navigate(segments, extras);
  }

  openTagsViewPage(extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigate([...this.baseDmsPath(), TABS_ROUTES.tags.path], extras);
  }

  openTrashBinViewPage(extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigate([...this.baseDmsPath(), TABS_ROUTES.trashBin.path], extras);
  }

  openSearchViewPage(extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigate([...this.baseDmsPath(), TABS_ROUTES.folders.path, DMS_SEARCH_PATH], extras);
  }

  getFileInternalLink(fileId: string, openPreview = true): string {
    const segments = [location.origin, ...this.baseDmsPath(), DMS_FILE_SEGMENT_PATH, fileId];
    const url = segments.join('/');
    const params = new URLSearchParams();
    if (!openPreview) {
      params.set(IrisDMSFileQueryParam.Action, IrisDMSFileQueryParamAction.OpenInfo);
    } else {
      params.set(IrisDMSFileQueryParam.Action, IrisDMSFileQueryParamAction.Link);
    }
    return `${url}?${params.toString()}`;
  }

  getFileDirectLink(file: IrisDMSFileI): string {
    const segments = [location.origin, ...this.baseDmsPath(), TABS_ROUTES.folders.path, file.parentId, DMS_FILES_PATH];
    const params = new URLSearchParams();
    params.set(IrisDMSFileQueryParam.File, file.id);
    params.set(IrisDMSFileQueryParam.Action, IrisDMSFileQueryParamAction.Link);
    segments.push(`?${params.toString()}`);
    return segments.join('/');
  }

  getFolderInternalLink(folderId: string): string {
    const segments = [location.origin, ...this.baseDmsPath(), TABS_ROUTES.folders.path, folderId, DMS_FILES_PATH];
    return segments.join('/');
  }

  replaceProjectIdInUrl(extras?: NavigationExtras): Promise<boolean> {
    const urlTree = this.router.parseUrl(this.router.url);
    const replaceCount = this.hasProjectIdInUrl() ? 1 : 0;
    if (!this.globalSandbox.currentProjectId()) {
      urlTree.root.children.primary.segments.splice(this.projectSegmentIndex, replaceCount);
    } else {
      const projectSegment = new UrlSegment(this.globalSandbox.currentProjectId().toString(), {});
      urlTree.root.children.primary.segments.splice(this.projectSegmentIndex, replaceCount, projectSegment);
    }
    return this.router.navigateByUrl(urlTree, { replaceUrl: true, ...extras });
  }

  hasProjectIdInUrl(): boolean {
    const urlTree = this.router.parseUrl(this.router.url);
    return urlTree.root.children.primary.segments.length > 1 && !isNaN(+urlTree.root.children.primary.segments[this.projectSegmentIndex].path);
  }
}
